<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

const emit = defineEmits<{
  rate: [rating: number]
}>()

type RatingForm = {
  rating: number | undefined
}

const form: RatingForm = reactive({
  rating: undefined,
})

const ratingMouseHover = ref<number | null>(null)
const getRatingInputId = (rating: number): string => `rating-input-rating-${rating}`
const starIconClasses = (rating: number): Record<string, boolean> => {
  return {
    'rating-input__star-icon--hover': ratingMouseHover.value !== null && rating <= ratingMouseHover.value,
    'rating-input__star-icon--selected': form.rating !== undefined && rating <= form.rating,
  }
}

const setRating = (rating: number): void => {
  form.rating = rating
  emit('rate', rating)
}

const setRatingKeyDown = (rating: number, { key }: KeyboardEvent): void => {
  if (key === 'Space' || key === 'Enter') {
    setRating(rating)
  }
}

const rules = {
  rating: { required },
}

const v = useVuelidate(rules, form)
</script>

<template>
  <div class="rating-input">
    <div>
      <template
        v-for="rating in 5"
        :key="rating"
      >
        <label
          aria-hidden="true"
          tabindex="0"
          :for="getRatingInputId(rating)"
          :data-test="getRatingInputId(rating)"
          class="rating-input__star"
          @click="setRating(rating)"
          @keydown="setRatingKeyDown(rating, $event)"
          @mouseover="ratingMouseHover = rating"
          @mouseleave="ratingMouseHover = null"
        >
          <Icon
            name="fa6-solid:star"
            size="30"
            class="rating-input__star-icon"
            :class="starIconClasses(rating)"
          />
          <span class="sr-only">Rate {{ rating }} out of 5</span>
        </label>
        <input
          :id="getRatingInputId(rating)"
          v-model="form.rating"
          name="rating"
          type="radio"
          :value="rating"
          tabindex="-1"
          class="sr-only"
          :aria-label="`${rating} star${ rating === 1 ? '' : 's' } out of 5`"
        >
      </template>
    </div>
    <UiFormError
      v-if="v.rating.$error"
      class="mt-0"
      message="Rating missing"
      data-test="rating-input-input-error"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.rating-input {
  @extend %flex-column-center;

  margin-bottom: var(--app-spacer-14);
  gap: var(--app-spacer-12);
}

.rating-input__star {
  display: inline-block;
  padding: var(--app-spacer-2);
  cursor: pointer;

  &:focus {
    outline-color: var(--app-color-sendcloud-focus);
  }
}

.rating-input__star:active {
  position: relative;
  top: var(--app-spacer-2);
}

.rating-input__star-icon {
  color: var(--app-color-rating-empty);
  transition: color 0.2s ease;
}

.rating-input__star-icon--hover,
.rating-input__star-icon--selected {
  color: var(--app-color-rating-filled);
}
</style>
